// Illinois State University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Illinois State's Common Data Set 2025-2026 (posted on prpa.illinoisstate.edu,
//    the planning and research office site): C1 applicants, admits and enrollees for Fall 2025 with
//    the residency table, C21/C22 plans (no early decision, no early action).
//    Quirk: the C8 boxes for fall 2027 mark the SAT and ACT as "not
//    considered" and C8A answers No, while C8F and the admissions page say
//    scores are optional and are considered if sent. The policy here comes
//    from the admissions page, and the CDS C8 line is left out.
//  - illinoisstate.edu Admissions: Freshman Admission Requirements (test
//    policy, what admissions considers) and Admissions Deadlines (freshman
//    fall term table). The deadlines page publishes no decision dates.
//  - Financial Aid Office, 2026-2027 undergraduate Cost of Attendance (general
//    undergraduate, on campus). It is used instead of CDS G1/G5 because it
//    gives whole-dollar items and totals; the CDS lists slightly different
//    figures (tuition $12,669, fees $4,146.30, food and housing $12,460).
//  - College Scorecard API, school id 145813 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Illinois State Common Data Set 2025-2026',
        url: 'https://prpa.illinoisstate.edu/downloads/CDS_2025-2026_ILSTU_FINAL2.pdf',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Illinois State Admissions, Freshman Admission Requirements',
        url: 'https://illinoisstate.edu/admissions/freshman-requirements/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Illinois State Admissions, Admissions Deadlines',
        url: 'https://illinoisstate.edu/apply/deadlines/',
        read: '2026-10-01',
    },
    coa: {
        label: 'Illinois State Financial Aid, 2026-2027 Undergraduate Cost of Attendance',
        url: 'https://financialaid.illinoisstate.edu/paying/2026-2027-undergraduate/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?145813-Illinois-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'illinois-state-university',
    kbId: 'illinois_state_university',
    name: 'Illinois State University',
    shortName: 'Illinois State',
    location: 'Normal, IL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Illinois State graphic standards); decoration only.
    branding: {
        primary: '#CE1126',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/illinois_state_university_logo.png',
    },
    faq: {
        answer: { text: 'Illinois State University admitted 85.5% of first-year applicants for fall 2025, 20,382 of the 23,826 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
        questions: [
            { q: 'What is the Illinois State University acceptance rate?', a: 'For fall 2025 entry, Illinois State admitted 20,382 of 23,826 first-year applicants, an acceptance rate of 85.5%, and 4,253 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the Illinois State application deadline?', a: 'For fall 2027 entry, the application deadline is April 1, and the documents needed for a decision are due the same day. The priority window runs August 1 to November 1, and applying in it gives you the best consideration for scholarships and for competitive or limited-enrollment majors. Illinois State says these dates may change without notice, and its deadlines page lists no decision dates.', source: 'deadlines' },
            { q: 'Does Illinois State have early action?', a: 'No. Illinois State has no early action or early decision plan. November 1 is a priority deadline, an earlier filing date rather than an early round.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'What is the Illinois State acceptance rate for out-of-state students?', a: 'For fall 2025, Illinois State admitted 1,007 of 1,245 out-of-state first-year applicants, 80.9%. Most applicants come from Illinois: 22,162 of the 23,826.', source: 'cds', where: 'section C1, residency table' },
            { q: 'Does Illinois State require the SAT or ACT?', a: 'No. You do not need to send an SAT or ACT score. If you do, admissions considers the scores and subscores, and Illinois State says a score cannot affect your standing negatively. Without a score, you must send your high school transcript to complete the application.', source: 'requirements' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 23826,
        admitted: 20382,
        enrolled: 4253,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 22162, admitted: 19001 },
            { group: 'Out-of-state', applied: 1245, admitted: 1007 },
            { group: 'International', applied: 382, admitted: 345 },
            { group: 'Unknown', applied: 37, admitted: 29 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Illinois State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Illinois State has no early decision or early action plan. Its November 1 priority deadline is an earlier filing date, not an early decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'April 1' },
        rows: [
            { plan: 'Priority deadline', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Application deadline', deadline: 'April 1', decision: null, binding: false },
        ],
        note: 'The priority window runs August 1 to November 1. Completing your application, with accurate information, by the priority deadline gives the best consideration for scholarships and for admission to competitive or limited-enrollment majors. Documents required for a decision are due April 1, and final documents for enrollment by July 15. Illinois State says the fall deadlines may change without notice, and it does not publish decision dates.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'You do not need to send an SAT or ACT score. If you do, admissions considers the scores and subscores, and Illinois State says a score cannot affect your standing negatively. If you apply without a score, you must send your high school transcript to complete the application, and a personal statement may be requested if your application needs more review.',
        source: 'requirements',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [12670, 25338] },
            { item: 'Fees', amounts: [4146, 4146] },
            { item: 'Food and housing (on campus)', amounts: [12902, 12902] },
            { item: 'Books and supplies', amounts: [830, 830] },
            { item: 'Transportation', amounts: [2452, 2452] },
            { item: 'Personal expenses', amounts: [3422, 3422] },
        ],
        totals: [36422, 49090],
        note: 'For a general undergraduate living on campus and taking 15 hours each semester, fall and spring. Illinois State sets these tables by the term you first enroll; this one is for students who started in fall 2026, spring 2027 or summer 2027. The College of Business, Computer Science, Cybersecurity, Engineering and the Mennonite College of Nursing have their own cost tables.',
        source: 'coa',
        netPrice: {
            average: 17829,
            byIncome: [
                { income: '$0 to $30,000', amount: 12040 },
                { income: '$30,001 to $48,000', amount: 11006 },
                { income: '$48,001 to $75,000', amount: 12488 },
                { income: '$75,001 to $110,000', amount: 21505 },
                { income: 'Over $110,000', amount: 27183 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Illinois State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
